import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import { bulkDuplicateModelTasks } from "services/models/modelDetails/modelTasks";

/**
 * Duplicates the whole selection in one request. The server does the copying
 * loop, so 30 selected tasks is one round trip rather than 30.
 *
 * Resolves to the new task IDs, so the caller can draw attention to the copies
 * rather than leaving the user to hunt for them in a 400 row list.
 */
const useBulkTaskDuplicate = ({ modelVersionId, onDuplicated }) => {
	const dispatch = useDispatch();
	const [isDuplicating, setIsDuplicating] = useState(false);

	const duplicateTasks = useCallback(
		async (taskIds) => {
			setIsDuplicating(true);
			try {
				const response = await bulkDuplicateModelTasks({
					modelVersionId,
					taskIds,
				});
				if (!response?.status) {
					dispatch(
						showError(
							response?.data?.title ||
								response?.data ||
								"Could not duplicate the selected tasks"
						)
					);
					return null;
				}
				const skipped = response.data?.skippedIDs?.length ?? 0;
				if (skipped > 0) {
					dispatch(
						showError(`${skipped} of ${taskIds.length} could not be duplicated`)
					);
				}
				await onDuplicated();
				return response.data?.createdTaskIDs ?? [];
			} finally {
				setIsDuplicating(false);
			}
		},
		[dispatch, modelVersionId, onDuplicated]
	);

	return { duplicateTasks, isDuplicating };
};

export default useBulkTaskDuplicate;
